/**
 * PrintService - Serviço de impressão para pedidos
 * Usa iframe oculto para enviar impressão diretamente
 */
class PrintService {
  constructor() {
    this.lastPrintStatus = null;
  }

  /**
   * Formata o endereço do pedido
   */
  formatAddress(address) {
    if (!address) return '';
    const parts = [];
    if (address.street) parts.push(address.street);
    if (address.number) parts.push(address.number);
    if (address.complement) parts.push(address.complement);
    if (address.neighborhood) parts.push(address.neighborhood);
    if (address.city) parts.push(address.city);
    return parts.join(', ');
  }

  /**
   * Gera o HTML formatado para impressão térmica (80mm) - NEGRITO
   */
  generateOrderHTML(orderData) {
    const isDelivery = orderData.delivery_type === 'delivery';
    const storeName = orderData.store_name || 'ADEGGA DELIVERY';
    
    // Calculate totals
    const itemsSubtotal = (orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const deliveryFee = parseFloat(orderData.delivery_fee) || 0;
    const discount = parseFloat(orderData.discount) || 0;
    const total = orderData.total || (itemsSubtotal + deliveryFee - discount);
    
    // Format address
    const addressStr = orderData.address ? this.formatAddress(orderData.address) : (orderData.customer_address || '');
    
    const items = (orderData.items || []).map(item => `
      <tr>
        <td style="padding: 8px 0; border-bottom: 2px dotted #000;">
          <strong>${item.quantity}x ${item.product_name}</strong>
          ${item.variant_name ? `<br><span style="font-size: 12px;">• ${item.variant_name}</span>` : ''}
          ${item.notes ? `<br><span style="font-size: 12px; font-style: italic;">Obs: ${item.notes}</span>` : ''}
        </td>
        <td style="padding: 8px 0; border-bottom: 2px dotted #000; text-align: right; white-space: nowrap;">
          <strong>R$ ${(item.unit_price * item.quantity).toFixed(2)}</strong>
        </td>
      </tr>
    `).join('');

    return `
      <div style="font-family: 'Courier New', Courier, monospace; font-size: 14px; font-weight: bold; width: 280px; padding: 10px; margin: 0 auto; color: #000; background: #fff;">
        <div style="text-align: center; font-size: 18px; margin-bottom: 5px;">${storeName}</div>
        ${orderData.store_whatsapp ? `<div style="text-align: center; font-size: 14px; margin-bottom: 15px;">Tel: ${orderData.store_whatsapp}</div>` : ''}
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>
        
        <div style="font-size: 20px;">PEDIDO #${orderData.order_number}</div>
        <div style="font-size: 14px; margin-top: 5px;">${new Date(orderData.created_at).toLocaleString('pt-BR')}</div>
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="margin: 8px 0; font-size: 14px;">CLIENTE: ${orderData.customer_name}</div>
        <div style="margin: 8px 0; font-size: 14px;">TEL: ${orderData.customer_phone}</div>
        <div style="margin: 8px 0; font-size: 14px;">TIPO: ${isDelivery ? '★★★ ENTREGA ★★★' : '★★★ RETIRADA ★★★'}</div>

        ${isDelivery && addressStr ? `
          <div style="background: #eee; padding: 12px; margin: 12px 0;">
            <div style="margin-bottom: 8px;">📍 ENDERECO:</div>
            <div>${addressStr}</div>
            ${orderData.address?.reference ? `<div style="margin-top: 8px;">REF: ${orderData.address.reference}</div>` : ''}
          </div>
        ` : ''}

        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
        
        <div style="font-size: 16px; margin-bottom: 10px;">📦 ITENS:</div>
        <table style="width: 100%; border-collapse: collapse;">
          ${items}
        </table>
        
        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
          <span>SUBTOTAL:</span>
          <span>R$ ${itemsSubtotal.toFixed(2)}</span>
        </div>
        ${isDelivery ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px; color: #ff6b00;">
            <span>TAXA ENTREGA:</span>
            <span>R$ ${deliveryFee.toFixed(2)}</span>
          </div>
        ` : ''}
        ${discount > 0 ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
            <span>DESCONTO:</span>
            <span>-R$ ${discount.toFixed(2)}</span>
          </div>
        ` : ''}
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 24px;">
          <span>💰 TOTAL:</span>
          <span>R$ ${total.toFixed(2)}</span>
        </div>
        
        <div style="border-top: 3px solid #000; margin: 12px 0;"></div>

        <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
          <span>PAGAMENTO:</span>
          <span>${orderData.payment_method === 'pix' ? '📱 PIX' : orderData.payment_method === 'cash' ? '💵 DINHEIRO' : '💳 CARTAO'}</span>
        </div>
        ${orderData.payment_method === 'cash' && orderData.change_for ? `
          <div style="display: flex; justify-content: space-between; margin: 8px 0; font-size: 16px;">
            <span>TROCO PARA:</span>
            <span>R$ ${orderData.change_for}</span>
          </div>
        ` : ''}

        ${orderData.notes ? `
          <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
          <div style="font-size: 14px;">📝 OBS: ${orderData.notes}</div>
        ` : ''}

        <div style="border-top: 3px dashed #000; margin: 12px 0;"></div>
        
        <div style="text-align: center; font-size: 14px;">
          <div>OBRIGADO PELA PREFERENCIA!</div>
          <div style="margin-top: 5px;">★ ADEGGA DELIVERY ★</div>
        </div>
      </div>
    `;
  }

  /**
   * Método principal de impressão usando iframe oculto
   * @param {Object} orderData - Dados do pedido
   * @returns {Promise<{success: boolean, method: string, error?: string}>}
   */
  async print(orderData) {
    return new Promise((resolve) => {
      try {
        // Remove existing print frame if any
        const existingFrame = document.getElementById('auto-print-frame');
        if (existingFrame) {
          existingFrame.remove();
        }

        // Create hidden iframe
        const iframe = document.createElement('iframe');
        iframe.id = 'auto-print-frame';
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.overflow = 'hidden';
        
        document.body.appendChild(iframe);

        const printContent = `
          <!DOCTYPE html>
          <html>
          <head>
            <meta charset="UTF-8">
            <title>Pedido #${orderData.order_number}</title>
            <style>
              @page { size: 80mm auto; margin: 0; }
              @media print { 
                html, body { width: 80mm; margin: 0; padding: 0; }
              }
              * { box-sizing: border-box; margin: 0; padding: 0; }
              body { background: white; }
            </style>
          </head>
          <body>
            ${this.generateOrderHTML(orderData)}
          </body>
          </html>
        `;

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(printContent);
        doc.close();

        // Wait for content to load then print
        iframe.onload = () => {
          setTimeout(() => {
            try {
              iframe.contentWindow.focus();
              iframe.contentWindow.print();
              
              this.lastPrintStatus = 'success';
              resolve({ success: true, method: 'iframe' });
              
              // Cleanup after delay
              setTimeout(() => {
                const frame = document.getElementById('auto-print-frame');
                if (frame) frame.remove();
              }, 2000);
            } catch (printError) {
              console.error('Print error:', printError);
              this.lastPrintStatus = 'error';
              resolve({ success: false, method: 'iframe', error: printError.message });
            }
          }, 300);
        };

        // Fallback timeout
        setTimeout(() => {
          try {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            this.lastPrintStatus = 'success';
            resolve({ success: true, method: 'iframe-fallback' });
          } catch (e) {
            this.lastPrintStatus = 'error';
            resolve({ success: false, method: 'iframe-fallback', error: e.message });
          }
        }, 1500);

      } catch (error) {
        this.lastPrintStatus = 'error';
        resolve({ success: false, method: 'iframe', error: error.message });
      }
    });
  }

  /**
   * Abre uma nova janela com o conteúdo formatado para impressão manual
   * Fallback quando iframe não funciona
   */
  openPrintWindow(orderData) {
    const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Pedido #${orderData.order_number}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    @media print { 
      body { width: 80mm; }
      .no-print { display: none !important; }
    }
    body { font-family: 'Courier New', monospace; margin: 0; padding: 20px; background: #f0f0f0; }
    .no-print { background: #ff6b00; color: white; padding: 20px; text-align: center; margin-bottom: 20px; max-width: 320px; margin: 0 auto 20px; border-radius: 8px; }
    .no-print button { background: white; color: #ff6b00; border: none; padding: 15px 40px; font-size: 18px; font-weight: bold; cursor: pointer; border-radius: 6px; }
    .receipt { max-width: 320px; margin: 0 auto; background: white; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
  </style>
</head>
<body>
  <div class="no-print">
    <p style="margin-bottom: 15px; font-weight: bold; font-size: 16px;">IMPRIMIR PEDIDO #${orderData.order_number}</p>
    <button onclick="window.print()">🖨️ CLIQUE PARA IMPRIMIR</button>
  </div>
  <div class="receipt">
    ${this.generateOrderHTML(orderData)}
  </div>
</body>
</html>
    `;
    
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const newWindow = window.open(url, '_blank', 'width=400,height=700');
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    
    return newWindow !== null;
  }

  /**
   * Gera texto simples do pedido
   */
  generateText(orderData) {
    const isDelivery = orderData.delivery_type === 'delivery';
    const addressStr = orderData.address ? this.formatAddress(orderData.address) : (orderData.customer_address || '');
    
    // Calculate totals
    const itemsSubtotal = (orderData.items || []).reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
    const deliveryFee = parseFloat(orderData.delivery_fee) || 0;
    const discount = parseFloat(orderData.discount) || 0;
    const total = orderData.total || (itemsSubtotal + deliveryFee - discount);
    
    return `
================================================
         ${orderData.store_name || 'ADEGGA DELIVERY'}
================================================

PEDIDO #${orderData.order_number}
${new Date(orderData.created_at).toLocaleString('pt-BR')}

CLIENTE: ${orderData.customer_name}
TEL: ${orderData.customer_phone}
TIPO: ${isDelivery ? '*** ENTREGA ***' : '*** RETIRADA ***'}
${isDelivery && addressStr ? `\nENDERECO: ${addressStr}` : ''}

------------------------------------------------
ITENS:
${(orderData.items || []).map(item => `${item.quantity}x ${item.product_name}  R$ ${(item.unit_price * item.quantity).toFixed(2)}`).join('\n')}
------------------------------------------------

SUBTOTAL:     R$ ${itemsSubtotal.toFixed(2)}
${deliveryFee > 0 ? `TAXA ENTREGA: R$ ${deliveryFee.toFixed(2)}` : ''}
${discount > 0 ? `DESCONTO:    -R$ ${discount.toFixed(2)}` : ''}

================================================
TOTAL:        R$ ${total.toFixed(2)}
================================================

PAGAMENTO: ${orderData.payment_method === 'pix' ? 'PIX' : orderData.payment_method === 'cash' ? 'DINHEIRO' : 'CARTAO'}
${orderData.payment_method === 'cash' && orderData.change_for ? `TROCO PARA: R$ ${orderData.change_for}` : ''}
${orderData.notes ? `\nOBS: ${orderData.notes}` : ''}

OBRIGADO PELA PREFERENCIA!
ADEGGA DELIVERY
    `.trim();
  }

  /**
   * Copia o texto do pedido para a área de transferência
   */
  copyToClipboard(orderData) {
    const text = this.generateText(orderData);
    navigator.clipboard.writeText(text);
    return true;
  }

  /**
   * Baixa o pedido como arquivo TXT
   */
  downloadTxt(orderData) {
    const text = this.generateText(orderData);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pedido-${orderData.order_number}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return true;
  }
}

// Singleton
const printService = new PrintService();
export default printService;
